import AIRefineIcon from '@assets/icons/AIRefineIcon'
import UndoIcon from '@assets/icons/UndoIcon'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { Selectable } from '@shared/filters/selections'
import useUpgradeLicense from '@shared/licensing/useUpgradeLicense'
import { AppPermission } from '@shared/permissions'
import stripHtml from '@shared/utils/stripHtml'
import { T, useT } from '@transifex/react'
import { useFormContext, useWatch } from 'react-hook-form'
import useAwardMessageRefinement from './useAwardMessageRefinement'
import { SendAwardFormData } from './useSendAwardForm'

type RefineAwardMessageButtonProps = {
  tenantQualities: Selectable[]
}

const RefineAwardMessageButton: React.FC<RefineAwardMessageButtonProps> = ({ tenantQualities }) => {
  const {
    refineMessage,
    deleteRefinementMessage,
    hasRefined,
    isPendingRefineMessage,
    isPendingUndoRefinedMessage,
  } = useAwardMessageRefinement({ tenantQualities })
  const { control } = useFormContext<SendAwardFormData>()
  const message = useWatch({ control, name: 'message' })
  const t = useT()
  const { interceptClick } = useUpgradeLicense()
  const handleClick = (event: React.MouseEvent) => {
    interceptClick(
      event as React.MouseEvent<HTMLAnchorElement, MouseEvent>,
      hasAppPermission =>
        hasAppPermission('refinementApp', AppPermission.RefinementAppUpgradeLicense),
      async () => await refineMessage(),
    )
  }

  return (
    <Stack direction="row" alignItems="center" useFlexGap gap={1}>
      <Button
        id="refineAwardButton"
        disabled={
          stripHtml(message).length === 0 || isPendingRefineMessage || isPendingUndoRefinedMessage
        }
        onClick={handleClick}
        variant="text"
        startIcon={<AIRefineIcon />}>
        <T _str="Refine with AI" />
      </Button>
      {hasRefined && (
        <Button
          onClick={async () => await deleteRefinementMessage()}
          aria-label={t('Undo refine')}
          color="primary"
          variant="text"
          id="btnUndoRefineAward">
          <UndoIcon />
        </Button>
      )}
    </Stack>
  )
}

export default RefineAwardMessageButton
